/**
 * Time-series activity chart for orgstats data.
 *
 * Lifted from the standalone /stats page (StatsPage.tsx) during Phase 4
 * of customer-health-v2 so the customer detail panel can render the same
 * charts in-place. The /stats page itself is being retired in this phase.
 */
import React, { useState } from 'react';
import './UsageActivityChart.css';

export type MetricKey = 'escalations' | 'reports' | 'videos' | 'video_duration_minutes' | 'actions';

export const METRICS: { value: MetricKey; label: string }[] = [
  { value: 'escalations', label: 'Escalations' },
  { value: 'reports', label: 'Reports' },
  { value: 'videos', label: 'Videos' },
  { value: 'video_duration_minutes', label: 'Video Duration (min)' },
  { value: 'actions', label: 'Actions' },
];

export const BUCKETS: { value: 'day' | 'week' | 'month'; label: string }[] = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
];

export interface TimeSeriesPoint {
  date: string;
  escalations?: number;
  reports?: number;
  videos?: number;
  video_duration_minutes?: number;
  actions?: number;
}

export interface TimeSeriesResponse {
  organization: string;
  bucket: 'day' | 'week' | 'month';
  from: string;
  to: string;
  points: TimeSeriesPoint[];
}

export function defaultBucketForRange(from: string, to: string): 'day' | 'week' | 'month' {
  const f = new Date(from).getTime();
  const t = new Date(to).getTime();
  const days = Math.max(1, Math.round((t - f) / (1000 * 60 * 60 * 24)));
  if (days <= 60) return 'day';
  if (days <= 180) return 'week';
  return 'month';
}

interface ActivityChartProps {
  points: TimeSeriesPoint[];
  metric: MetricKey;
  metricLabel: string;
}

export const ActivityChart: React.FC<ActivityChartProps> = ({ points, metric, metricLabel }) => {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  if (points.length === 0) {
    return <p className="stats-chart-empty">No activity in this range.</p>;
  }

  const W = 800, H = 280;
  const PAD_L = 48, PAD_R = 16, PAD_T = 16, PAD_B = 44;
  const plotW = W - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;

  const values = points.map(p => Number(p[metric] ?? 0));
  const maxVal = Math.max(1, ...values);
  const niceMax = (() => {
    const exp = Math.pow(10, Math.floor(Math.log10(maxVal)));
    const norm = maxVal / exp;
    let nice = 10;
    if (norm <= 1) nice = 1;
    else if (norm <= 2) nice = 2;
    else if (norm <= 5) nice = 5;
    return nice * exp;
  })();

  const xAt = (i: number) => PAD_L + (points.length === 1 ? plotW / 2 : (i / (points.length - 1)) * plotW);
  const yAt = (v: number) => PAD_T + plotH - (v / niceMax) * plotH;

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)} ${yAt(values[i]).toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L ${xAt(points.length - 1).toFixed(1)} ${PAD_T + plotH} L ${xAt(0).toFixed(1)} ${PAD_T + plotH} Z`;

  const yTicks = 4;
  const yTickValues = Array.from({ length: yTicks + 1 }, (_, i) => (niceMax * i) / yTicks);

  const xLabelStep = Math.max(1, Math.ceil(points.length / 6));
  const formatDate = (s: string) => {
    const d = new Date(s);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="stats-chart-wrapper">
      <svg
        className="stats-chart"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        onMouseLeave={() => setHoverIndex(null)}
      >
        {yTickValues.map((v, i) => {
          const y = yAt(v);
          return (
            <g key={i}>
              <line x1={PAD_L} x2={W - PAD_R} y1={y} y2={y} className="stats-chart-grid" />
              <text x={PAD_L - 8} y={y} className="stats-chart-axis-text" dominantBaseline="middle" textAnchor="end">
                {Number.isInteger(v) ? v.toLocaleString() : v.toFixed(1)}
              </text>
            </g>
          );
        })}
        {points.map((p, i) => {
          if (i % xLabelStep !== 0 && i !== points.length - 1) return null;
          return (
            <text key={`x-${i}`} x={xAt(i)} y={H - PAD_B + 18} className="stats-chart-axis-text" textAnchor="middle">
              {formatDate(p.date)}
            </text>
          );
        })}
        <path d={areaPath} className="stats-chart-area" />
        <path d={linePath} className="stats-chart-line" />
        {points.map((p, i) => (
          <circle
            key={`pt-${i}`}
            cx={xAt(i)}
            cy={yAt(values[i])}
            r={hoverIndex === i ? 5 : 3}
            className={`stats-chart-point${hoverIndex === i ? ' is-hover' : ''}`}
          />
        ))}
        {points.map((p, i) => {
          const cx = xAt(i);
          const half = points.length === 1 ? plotW / 2 : (plotW / (points.length - 1)) / 2;
          return (
            <rect
              key={`hit-${i}`}
              x={Math.max(PAD_L, cx - half)}
              y={PAD_T}
              width={Math.min(W - PAD_R - Math.max(PAD_L, cx - half), half * 2)}
              height={plotH}
              fill="transparent"
              onMouseEnter={() => setHoverIndex(i)}
            />
          );
        })}
        {hoverIndex !== null && (
          <g>
            <line
              x1={xAt(hoverIndex)} x2={xAt(hoverIndex)}
              y1={PAD_T} y2={PAD_T + plotH}
              className="stats-chart-hover-line"
            />
            <g transform={`translate(${Math.min(W - PAD_R - 140, Math.max(PAD_L, xAt(hoverIndex) + 8))}, ${PAD_T + 8})`}>
              <rect width="140" height="42" rx="4" className="stats-chart-tooltip-bg" />
              <text x="8" y="16" className="stats-chart-tooltip-date">{formatDate(points[hoverIndex].date)}</text>
              <text x="8" y="32" className="stats-chart-tooltip-value">
                {metricLabel}: {Number(points[hoverIndex][metric] ?? 0).toLocaleString()}
              </text>
            </g>
          </g>
        )}
      </svg>
    </div>
  );
};
